import { Box, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import { CheckCircleOutlined, ErrorOutline, Info, StopCircleOutlined } from '@mui/icons-material';
import { t } from 'i18next';
import { BatchExport } from '../../../../../integrations/exports/models/BatchExport';

export type ExportProgressProps = {
  exportJob: BatchExport;
  onCancel: () => void;
};

export const ExportCanceledByUser = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1.5,
        px: 1.5,
      }}
    >
      <ErrorOutline color="error" sx={{ mr: 1 }} />
      <Typography variant="body2" sx={{ color: 'error.main' }}>
        {t('exportFormats.exportProgress.canceledByUser')}
      </Typography>
    </Box>
  );
};

export const ExportIsDone = ({ exportedCount }: { exportedCount: number }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1.5,
        px: 1.5,
      }}
    >
      <CheckCircleOutlined color="success" sx={{ mr: 1 }} />
      <Typography variant="body2" sx={{ color: 'text.success' }}>
        {t('exportFormats.exportProgress.isDone', { exportedCount })}
      </Typography>
    </Box>
  );
};

export const ExportProgress = ({ exportJob, onCancel }: ExportProgressProps) => {
  const progress = Math.round((exportJob.getExportedCount() / exportJob.getTotalCount()) * 100);
  const isDone = exportJob.isFinished();

  if (exportJob.isCanceled()) {
    return <ExportCanceledByUser />;
  }

  if (isDone) {
    return <ExportIsDone exportedCount={exportJob.getExportedCount()} />;
  }

  return (
    <Box
      sx={{
        padding: 0,
        textAlign: 'right',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: '1px solid',
          borderTopColor: 'grey.300',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1.5,
            px: 1.5,
          }}
        >
          <Info color="primary" sx={{ mr: 1 }} />
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {t('exportFormats.exportProgress.inProgress', {
              exportedCount: exportJob.getExportedCount(),
              totalCount: exportJob.getTotalCount(),
            })}
          </Typography>
        </Box>
        <Tooltip title={t('exportFormats.exportProgress.cancelExportTooltip')}>
          <IconButton data-role="cancel-report-export" color="error" onClick={onCancel}>
            <StopCircleOutlined />
          </IconButton>
        </Tooltip>
      </Box>
      <LinearProgress
        sx={{
          height: 7,
          borderRadius: '0 0 3px 3px',
          border: 'none',
          '& .MuiLinearProgress-bar': {
            borderRadius: '0 0 3px 3px',
          },
        }}
        variant="determinate"
        value={progress}
      />
    </Box>
  );
};
